import type { PublicPluginAPI } from '@atlaskit/editor-common/types';
import type { AnalyticsPlugin } from '@atlaskit/editor-plugin-analytics';
import { fg } from '@atlaskit/platform-feature-flags';
import {
	AdfParseError,
	AdfParser,
	AdfRepairStrategy,
	AdfSchemaValidator,
} from '@atlassian/adf-parser';
import { AdfToolCallsDebugger } from '@atlassian/editor-ai-common/utils/adf-tool-calls-debugger';
import type {
	AiStreamingOrchestratorPlugin,
	StreamingCommand,
} from '@atlassian/editor-plugin-ai-streaming-orchestrator';
import { ROVO_CHAT_CLIENT_ID } from '@atlassian/rovo-content-bridge-api-commands/clients/rovo-chat-client';
import { InlineInputBoxAbortMessageCommand } from '@atlassian/rovo-content-bridge-api-commands/commands/inline-input-box-abort-message-command';
import type { RovoContentBridgeAPI } from '@atlassian/rovo-content-bridge-api/bridge-api';
import type { ClientId } from '@atlassian/rovo-content-bridge-api/client-types';

import type { ActionMetadata, ContentLocation } from '../types/content';
import { log } from '../utils/aifc-logging';
import { fireInfiniteLoopDetectedAnalyticsEvent } from '../utils/analytics';

import { getParserConfig } from './parser-config';
import {
	FinishAction,
	InitAction,
	PartialStreamParser,
	StreamAction,
} from './partial-stream-parser';

/**
 * The total amount of times we're happy to process the same toolcall command. If more sequential toolcalls exist then
 * what this value is set too, then we have detected a possible infinite loop and we will abort the stream.
 */
const MAX_INFINITE_LOOP_COUNT = 5;
const SKIP_INFINITE_LOOP_ACTIONS = ['insertNodeAfter', 'insertNodeBefore'];

class ToolCallProcessor {
	private actionId: string;
	private accumulated: string = '';
	private parser: AdfParser | undefined;

	constructor(
		private editorApi: PublicPluginAPI<AiStreamingOrchestratorPlugin>,
		private contentLocation: ContentLocation,
		command: StreamingCommand,
	) {
		const actionId = this.editorApi?.aiStreamingOrchestrator?.actions.initAction(command, {
			/**
			 * Always hide the loading screen as staging area always shows Rovo conversation
			 * and will already have a loading indicator.
			 */
			hideLoadingScreen: this.contentLocation === 'staging-area',
		});
		if (!actionId) {
			throw new Error('Failed to initialize streaming action for update');
		}
		this.actionId = actionId;
		log('Initialized editor streaming action', { streamType: 'update', actionId, command });

		const schema = this.editorApi.core.sharedState.currentState()?.schema;
		if (schema) {
			this.parser = new AdfParser({
				schemaValidator: new AdfSchemaValidator(schema),
				repairStrategy: new AdfRepairStrategy(schema),
				config: getParserConfig(),
			});
		}
	}

	stream(adf: string, isFinished: boolean) {
		if (!adf) {
			return;
		}

		if (this.parser) {
			try {
				this.parser.processChunk(adf, isFinished);
			} catch (error) {
				if (error instanceof AdfParseError) {
					// We want to ignore any parsing errors
				} else {
					// We still want to throw any other unexpected errors
					throw error;
				}
			}
			this.editorApi.aiStreamingOrchestrator?.actions.streamValue(
				this.actionId,
				JSON.stringify(this.parser.getPartialDocument()) ?? '',
			);
		} else {
			this.editorApi.aiStreamingOrchestrator?.actions.streamValue(this.actionId, adf);
		}

		this.accumulated += adf;
		log('Streaming content', { actionId: this.actionId, adf });
	}

	finish() {
		this.editorApi?.aiStreamingOrchestrator?.actions.finishAction(this.actionId);
		log('Finished action', { actionId: this.actionId, fullAdf: this.accumulated });
		this.accumulated = '';
	}
}

function assertUnreachable(_x: never) {}

export class DocumentUpdateCommandProcessor {
	private parser: PartialStreamParser;
	private currentToolCall: ToolCallProcessor | undefined;
	private currentToolCallName: string | null = null;
	private currentToolCallLocalId: string | null = null;
	private currentRawCommand: Record<string, unknown> | null = null;
	private currentStreamedValue: string = '';
	private accumulated: string = '';
	private lastAction: string | null;
	private hasDetectedInfiniteLoop: boolean = false;
	private loopsDetected: number = 0;
	private debugger: AdfToolCallsDebugger | null = null;
	private actionMetadata: ActionMetadata | null = null;

	constructor(
		private editorApi: PublicPluginAPI<[AnalyticsPlugin, AiStreamingOrchestratorPlugin]>,
		private contentLocation: ContentLocation,
		private clientId: ClientId,
		private rovoBridgeAPI: RovoContentBridgeAPI,
	) {
		this.parser = new PartialStreamParser();
		this.lastAction = null;
		this.debugger = AdfToolCallsDebugger.getInstance();
	}

	private reset() {
		this.accumulated = '';
		this.lastAction = null;
		this.currentToolCall = undefined;
		this.currentToolCallName = null;
		this.currentToolCallLocalId = null;
		this.currentRawCommand = null;
		this.currentStreamedValue = '';
		this.hasDetectedInfiniteLoop = false;
		this.loopsDetected = 0;
		this.parser.reset();
	}

	private createToolCallProcessor(toolCall: InitAction) {
		const type = toolCall.name;
		const localId = toolCall.localId ?? '';

		let command: StreamingCommand;
		switch (type) {
			case 'replaceNode':
			case 'insertNodeBefore':
			case 'insertNodeAfter':
			case 'removeNode':
				command = { type, localId };
				break;
			case 'moveNode':
				const anchorLocalId = toolCall.anchorLocalId;
				const relationship = toolCall.relationship;
				if (!localId || !anchorLocalId || !relationship) {
					return;
				}

				command = { type, localId, anchorLocalId, relationship };
				break;
			case 'appendNode':
			case 'appendNodeToEnd':
				command = { type };
				break;
			case 'wrap':
			case 'wrapNode':
				const wrapperType = toolCall.wrapperType;
				const wrapperAttrs = toolCall.wrapperAttrs;
				if (!wrapperType || !wrapperAttrs) {
					return;
				}

				command = { type, localId, wrapperType, wrapperAttrs };
				break;
			case 'setAttrs':
				const attrs = toolCall.attrs;
				if (!attrs) {
					return;
				}

				command = { type, localId, attrs };
				break;

			default:
				assertUnreachable(type);
				return;
		}

		return new ToolCallProcessor(this.editorApi, this.contentLocation, command);
	}

	streamChunk(
		chunk: string,
		isFinished: boolean,
		traceId?: string,
		singleInstrumentationId?: string,
	): void {
		// The last message contains a copy of the full, accumulated tool call structure. We
		// don't want to reprocess this, so we just return instead.
		if (isFinished) {
			return;
		}

		if (this.accumulated.length === 0) {
			this.reset();
			this.editorApi.aiStreamingOrchestrator?.actions.startSession(
				fg('platform_editor_ai_traceid_in_analytics_events') ? traceId : undefined,
				{
					singleInstrumentationId,
					promptVersion: fg('platform_editor_ai_promptversion_in_analytics')
						? this.actionMetadata?.promptVersion
						: undefined,
				},
			);
			this.debugger?.startSession(traceId, 'update');
		}

		this.accumulated += chunk;
		return this.stream(chunk, isFinished);
	}

	private stream(chunk: string, isFinished: boolean) {
		// Once we detect an infinite loop and call finish(), we won't be able to recover
		if (this.hasDetectedInfiniteLoop) {
			return;
		}
		const results = this.parser.parse(chunk);

		for (const result of results) {
			if (result instanceof InitAction) {
				if (result.localId) {
					if (
						this.lastAction === `${result.name}-${result.localId}` &&
						!SKIP_INFINITE_LOOP_ACTIONS.includes(result.name)
					) {
						this.loopsDetected++;
						if (this.loopsDetected >= MAX_INFINITE_LOOP_COUNT) {
							this.hasDetectedInfiniteLoop = true;
							fireInfiniteLoopDetectedAnalyticsEvent(this.editorApi, result);
							this.debugger?.recordSessionError(
								'infinite_loop_detected',
								`Infinite loop detected for ${result.name} on localId ${result.localId}`,
							);
							return;
						} else {
							this.initToolCallWithDebugger(result);
						}
					} else {
						this.loopsDetected = 0;
						this.initToolCallWithDebugger(result);
					}
					this.lastAction = `${result.name}-${result.localId}`;
				} else {
					this.lastAction = null;
					this.initToolCallWithDebugger(result);
				}
			} else if (result instanceof StreamAction) {
				this.currentToolCall?.stream(result.partialValue, isFinished);
				this.currentStreamedValue += result.partialValue;
			} else if (result instanceof FinishAction) {
				this.currentToolCall?.finish();
				if (this.currentToolCallName) {
					const fullRawCommand = this.currentRawCommand
						? {
								...this.currentRawCommand,
								...(this.currentStreamedValue ? { value: this.currentStreamedValue } : {}),
							}
						: null;
					this.debugger?.recordToolCallSuccess(
						this.currentToolCallName,
						this.currentToolCallLocalId,
						fullRawCommand,
					);
				}
				this.currentToolCallName = null;
				this.currentToolCallLocalId = null;
				this.currentRawCommand = null;
				this.currentStreamedValue = '';
			} else {
				throw new Error('Unknown action type from partial stream parser');
			}
		}
	}

	private static buildRawCommand(result: InitAction): Record<string, unknown> {
		const raw: Record<string, unknown> = { name: result.name };
		if (result.localId != null) {
			raw.localId = result.localId;
		}
		if (result.anchorLocalId != null) {
			raw.anchorLocalId = result.anchorLocalId;
		}
		if (result.relationship != null) {
			raw.relationship = result.relationship;
		}
		if (result.wrapperType != null) {
			raw.wrapperType = result.wrapperType;
		}
		if (result.wrapperAttrs != null) {
			raw.wrapperAttrs = result.wrapperAttrs;
		}
		if (result.attrs != null) {
			raw.attrs = result.attrs;
		}
		return raw;
	}

	private initToolCallWithDebugger(result: InitAction) {
		this.currentToolCallName = result.name;
		this.currentToolCallLocalId = result.localId ?? null;
		this.currentRawCommand = DocumentUpdateCommandProcessor.buildRawCommand(result);

		try {
			this.currentToolCall = this.createToolCallProcessor(result);
			if (!this.currentToolCall) {
				this.debugger?.recordToolCallError(
					result.name,
					result.localId ?? null,
					'invalid_command_found',
					`Failed to create tool call processor for ${result.name}: missing required attributes`,
					this.currentRawCommand,
				);
			}
		} catch (error) {
			const message = error instanceof Error ? error.message : String(error);
			this.debugger?.recordToolCallError(
				result.name,
				result.localId ?? null,
				'tool_call_init_failed',
				message,
				this.currentRawCommand,
			);
		}
	}

	async finish(): Promise<void> {
		if (this.hasDetectedInfiniteLoop) {
			this.rovoBridgeAPI.send(InlineInputBoxAbortMessageCommand, {
				receiverId: ROVO_CHAT_CLIENT_ID,
				senderId: this.clientId,
			});
			this.debugger?.endSession('errored');
			this.reset();
			await this.editorApi.aiStreamingOrchestrator?.actions.endSession();
		} else {
			this.debugger?.endSession('completed');
			this.accumulated = '';
			await this.editorApi.aiStreamingOrchestrator?.actions.endSession();
			this.lastAction = null;
			this.currentToolCall = undefined;
			this.currentToolCallName = null;
			this.currentToolCallLocalId = null;
			this.currentRawCommand = null;
			this.currentStreamedValue = '';
			this.parser.reset();
		}
	}

	abort(reason?: 'aborted' | 'timeout'): void {
		this.debugger?.endSession(reason ?? 'aborted');
		this.reset();
		this.editorApi.aiStreamingOrchestrator?.actions.abortSession();
	}

	setActionMetadata(metadata: ActionMetadata): void {
		this.actionMetadata = metadata;
		log('Set action metadata', { metadata });
	}
}
